import { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SECURITY NOTE: localStorage Usage for Authentication
 * 
 * This app intentionally uses localStorage for session tokens instead of httpOnly cookies.
 * This design decision was made because:
 * 
 * 1. Mobile Safari and iOS in-app browsers (Google, Instagram, etc.) aggressively
 *    clear cookies, causing users to be logged out constantly on mobile PWA.
 * 
 * 2. The alternative (httpOnly cookies with SameSite=None) required complex CORS
 *    configurations that still failed in many mobile browser contexts.
 * 
 * 3. For a PWA that prioritizes mobile UX, persistent login is critical.
 * 
 * Mitigations in place:
 * - Tokens are cryptographically random UUIDs generated server-side
 * - Sessions expire after 90 days of inactivity
 * - HTTPS-only communication
 * - Token refresh on each authenticated request
 * - XSS protection via React's default escaping and CSP headers
 */
const TOKEN_KEY = "wornwell_session_token";
const USER_CACHE_KEY = "wornwell_user_cache";
const CACHE_TIMESTAMP_KEY = "wornwell_cache_timestamp";
const LAST_EMAIL_KEY = "wornwell_last_email";
const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes - revalidate in background after this

// Helper to get auth headers
export function getAuthHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    return { "Authorization": `Bearer ${token}` };
  }
  return {};
}

// Helper for authenticated fetch requests
export async function authFetch(url, options = {}) {
  const headers = {
    ...options.headers,
    ...getAuthHeaders(),
  };
  
  return fetch(url, {
    ...options,
    headers,
    credentials: "include", // Still include cookies as fallback
  });
}

// Get cached user data
function getCachedUser() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    const cachedUser = localStorage.getItem(USER_CACHE_KEY);
    const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cachedUser && cacheTimestamp) {
      const parsedUser = JSON.parse(cachedUser);
      return parsedUser;
    }
  } catch (e) {
    console.error("Cache read error:", e);
  }
  return null;
}

// Check if cache needs background refresh
function shouldRefreshCache() {
  const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
  if (!cacheTimestamp) return true;
  return Date.now() - parseInt(cacheTimestamp, 10) > CACHE_MAX_AGE;
}

// Save user to cache
function cacheUser(userData) {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    // Store email for login hint
    if (userData?.email) {
      localStorage.setItem(LAST_EMAIL_KEY, userData.email);
    }
  } catch (e) {
    console.error("Cache write error:", e);
  }
}

// Clear cache (but keep last email for login hint)
function clearCache() {
  localStorage.removeItem(USER_CACHE_KEY);
  localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  // Don't remove LAST_EMAIL_KEY - we want to keep it for login hint
}

// Get last logged in email for login hint
export function getLastEmail() {
  return localStorage.getItem(LAST_EMAIL_KEY);
}

export function AuthProvider({ children }) {
  // Initialize with cached user for instant loading
  const [user, setUser] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(() => !getCachedUser());

  // Note: API_URL, TOKEN_KEY, authFetch, clearCache, getCachedUser, shouldRefreshCache, cacheUser
  // are module-level constants/functions that never change, so they don't need to be dependencies
  const checkAuth = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem(TOKEN_KEY);
    
    // No token = definitely not logged in
    if (!token) {
      setUser(null);
      setLoading(false);
      clearCache();
      return;
    }
    
    // If we have cached user and don't need refresh, use it immediately
    const cachedUser = getCachedUser();
    if (cachedUser && !forceRefresh && !shouldRefreshCache()) {
      setUser(cachedUser);
      setLoading(false);
      return;
    }
    
    // If we have cached user but need refresh, show cached first then refresh in background
    if (cachedUser && !forceRefresh) {
      setUser(cachedUser);
      setLoading(false);
    }
    
    try {
      const response = await authFetch(`${API_URL}/api/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        cacheUser(userData);
        
        // Refresh session in background to extend expiry
        authFetch(`${API_URL}/api/auth/refresh`, { method: "POST" }).catch(() => {});
      } else {
        // Session invalid - clear everything
        localStorage.removeItem(TOKEN_KEY);
        clearCache();
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      // On network error, keep cached user if available
      if (!cachedUser) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    
    // If we have cached user, we're already showing them - just validate in background
    if (user) {
      // Background validation
      checkAuth(false);
    } else {
      checkAuth(true);
    }
  }, []);

  const login = (loginHint) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    let authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    
    // If we have a login hint (user's email), pass it to pre-select their account
    if (loginHint) {
      authUrl += `&login_hint=${encodeURIComponent(loginHint)}`;
    }
    
    window.location.href = authUrl;
  };

  const logout = async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    localStorage.removeItem(TOKEN_KEY);
    clearCache();
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    cacheUser(userData);
  };

  // Function to save token (called from AuthCallback)
  const saveToken = (token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, checkAuth, saveToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
